import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
import {
  getProjectStartGuideStatus,
  getServerStartGuideStatus,
  getStartGuideStatusSnapshot,
  setServerStartGuideStatus,
  setStartGuideCollapsed,
  setStartGuideCompletionNoticeDismissed,
  setStartGuideDismissed,
  setStartGuideNotice,
  subscribeStartGuideStatus,
} from "./startGuideClientStatus";
import { isServerStartGuideStatus } from "./startGuideStatusSource";
import {
  countRequiredProgress,
  isStartGuideSkippable,
  orderStepsByServerKeys,
  resolveStartGuideSteps,
} from "./resolveStartGuideSteps";
import { loadStartGuide, requestStartGuideSkip } from "./startGuideApi";

function buildStepsContent(content) {
  const stepsContent = {};

  Object.entries(content.steps).forEach(([id, step]) => {
    const definition = content.defaultSteps.find((item) => item.id === id);
    const go = ({ navigate }) => {
      if (step.to) navigate(step.to);
    };
    const actions = [
      {
        id: `${id}-go`,
        label: step.actionLabel,
        variant: "primary",
        onClick: go,
      },
    ];

    (step.secondaryActions || []).forEach((action, index) => {
      actions.push({
        id: `${id}-secondary-${index}`,
        label: action.label,
        to: action.to,
        href: action.href,
      });
    });

    if (definition?.category !== "learn") {
      const learnMoreLabel = i18n.t("corePages.startGuide.learnMore");
      const learnMore = step.learnMore || { label: learnMoreLabel, to: "/help" };
      actions.push({
        id: `${id}-learn`,
        label: learnMore.label || learnMoreLabel,
        to: learnMore.to,
        href: learnMore.href,
      });
    }

    const prerequisiteId = definition?.requires?.[0];
    const prerequisite = prerequisiteId ? content.steps[prerequisiteId] : null;
    if (prerequisite?.to) {
      actions.push({
        id: `${id}-prerequisite`,
        label: i18n.t("corePages.startGuide.openPrerequisite", { name: prerequisite.name }),
        to: prerequisite.to,
      });
    }

    stepsContent[id] = {
      ...step,
      actions,
    };
  });

  return stepsContent;
}

function applyStartGuideResult(project, result, { replaceOnError = false } = {}) {
  if (!project || !result) return;
  const previous = getServerStartGuideStatus(project);
  if (
    !replaceOnError &&
    result.error &&
    previous?.loaded &&
    Object.keys(previous.steps || {}).length > 0
  ) {
    setServerStartGuideStatus(project, { ...previous, error: result.error });
    return;
  }
  setServerStartGuideStatus(project, result);
}

export function useProjectStartGuide(project, content) {
  const location = useLocation();
  const { i18n: i18nInstance } = useTranslation();
  const language = i18nInstance.language;
  const companyId = useSelector(
    (state) => state.user?.user?.company?.id || state.user?.selectedCompanyId || null
  );
  const snapshot = useSyncExternalStore(
    subscribeStartGuideStatus,
    getStartGuideStatusSnapshot,
    getStartGuideStatusSnapshot
  );
  const projectStatus = snapshot.projects[project] || getProjectStartGuideStatus(project);
  const serverStatus = snapshot.server[project] || getServerStartGuideStatus(project);

  const appliedCompany = useRef(undefined);

  useEffect(() => {
    if (!isServerStartGuideStatus() || !project) return undefined;
    const companyChanged = appliedCompany.current !== companyId;
    const replaceOnError = appliedCompany.current !== undefined && companyChanged;
    let cancel = false;
    const run = (fresh) => {
      loadStartGuide(project, { fresh }).then((result) => {
        if (cancel) return;
        applyStartGuideResult(project, result, { replaceOnError });
        appliedCompany.current = companyId;
      });
    };
    run(companyChanged);
    const onFocus = () => run(false);
    window.addEventListener("focus", onFocus);
    return () => {
      cancel = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [companyId, location.pathname, project]);

  const stepDefinitions = useMemo(() => {
    if (content.followServerOrder && serverStatus?.loaded) {
      return orderStepsByServerKeys(content.defaultSteps, serverStatus.steps);
    }
    return content.defaultSteps;
  }, [content.defaultSteps, content.followServerOrder, serverStatus]);

  const steps = useMemo(
    () =>
      resolveStartGuideSteps({
        steps: stepDefinitions,
        clientSteps: projectStatus.steps,
        serverStatus,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepDefinitions, projectStatus.steps, serverStatus, language]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stepsContent = useMemo(() => buildStepsContent(content), [content, language]);
  const setupComplete = countRequiredProgress(steps).complete;

  useEffect(() => {
    if (!setupComplete && projectStatus.completionNoticeDismissed) {
      setStartGuideCompletionNoticeDismissed(project, false);
    }
  }, [project, projectStatus.completionNoticeDismissed, setupComplete]);

  const skipStep = useCallback(
    (stepId) => {
      const definition = content.defaultSteps.find((step) => step.id === stepId);
      if (!isStartGuideSkippable(definition)) return;
      const previous = getServerStartGuideStatus(project) || {
        project,
        show_user_guide: true,
        steps: {},
        loaded: true,
        error: null,
      };
      const current = previous.steps?.[stepId] || {};
      setServerStartGuideStatus(project, {
        ...previous,
        error: null,
        loaded: true,
        steps: {
          ...previous.steps,
          [stepId]: {
            complete: false,
            skipped: true,
            locked: Boolean(current.locked),
            lockReason: current.lockReason || "",
          },
        },
      });
      requestStartGuideSkip(project, stepId).then((result) => {
        if (result?.error) {
          setServerStartGuideStatus(project, { ...previous, error: result.error });
          return;
        }
        if (result?.status) setServerStartGuideStatus(project, result.status);
      });
    },
    [content.defaultSteps, project]
  );

  return {
    steps,
    stepsContent,
    loading: !serverStatus?.loaded,
    statusError: serverStatus?.error || "",
    projectGuideVisible: serverStatus?.loaded ? Boolean(serverStatus.show_user_guide) : null,
    dismissed: Boolean(projectStatus.dismissed),
    collapsed: Boolean(projectStatus.collapsed),
    notice: projectStatus.notice,
    dismiss: () => setStartGuideDismissed(project, true),
    restore: () => setStartGuideDismissed(project, false),
    setCollapsed: (collapsed) => setStartGuideCollapsed(project, collapsed),
    clearNotice: () => setStartGuideNotice(project, null),
    completionNoticeDismissed: Boolean(projectStatus.completionNoticeDismissed),
    dismissCompletionNotice: () => setStartGuideCompletionNoticeDismissed(project, true),
    skipStep,
    markRequiredVisit: () => {},
    usesClientStatus: !isServerStartGuideStatus(),
  };
}

export function notifyStartGuideSaved(project) {
  if (!project || !isServerStartGuideStatus()) return;
  loadStartGuide(project, { fresh: true }).then((result) => applyStartGuideResult(project, result));
}
